import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format, parseISO, startOfMonth, endOfMonth, formatDistanceToNow } from "date-fns";
import { Users, Phone, Calendar, Search, ChevronLeft, ChevronRight, Clock, Pencil, Trash2, Loader2, ChevronDown, X, LogIn, KeyRound } from "lucide-react";

const EmployeeList = () => {
  const { canManage, getAuthHeaders } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [employeeShifts, setEmployeeShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [scheduleMonth, setScheduleMonth] = useState(new Date());

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [posDropOpen, setPosDropOpen] = useState(false);
  const [showResetPwDialog, setShowResetPwDialog] = useState(false);
  const [resetPwEmployee, setResetPwEmployee] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resettingPw, setResettingPw] = useState(false);

  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zip_code: "",
    positions: [],
    role: "employee",
    seniority_date: "",
  });

  useEffect(() => {
    fetchEmployees();
    fetchPositions();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: getAuthHeaders(),
        
      });
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const res = await axios.get(`${API}/positions`, { headers: getAuthHeaders() });
      setPositions(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchEmployeeSchedule = async (employeeId) => {
    setShiftsLoading(true);
    try {
      const start = format(startOfMonth(scheduleMonth), "yyyy-MM-dd");
      const end = format(endOfMonth(scheduleMonth), "yyyy-MM-dd");
      const response = await axios.get(
        `${API}/shifts?start_date=${start}&end_date=${end}&employee_id=${employeeId}`,
        { headers: getAuthHeaders() }
      );
      setEmployeeShifts(response.data);
    } catch (error) {
      console.error("Error fetching employee schedule:", error);
    } finally {
      setShiftsLoading(false);
    }
  };

  const handleViewSchedule = (employee) => {
    setSelectedEmployee(employee);
    setShowScheduleDialog(true);
    fetchEmployeeSchedule(employee.id);
  };

  useEffect(() => {
    if (selectedEmployee && showScheduleDialog) {
      fetchEmployeeSchedule(selectedEmployee.id);
    }
  }, [scheduleMonth]);

  const handleEditClick = (emp) => {
    setEditEmployee(emp);
    setEditForm({
      first_name: emp.first_name || "",
      last_name: emp.last_name || "",
      email: emp.email || "",
      phone: emp.phone || "",
      street: emp.street || "",
      city: emp.city || "",
      state: emp.state || "",
      zip_code: emp.zip_code || "",
      positions: emp.positions || [],
      role: emp.role || "employee",
      seniority_date: emp.seniority_date || emp.date_of_hire || "",
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editEmployee) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/employees/${editEmployee.id}`,
        editForm,
        { headers: getAuthHeaders() }
      );
      // Also update role if changed
      if (editForm.role !== editEmployee.role) {
        await axios.put(
          `${API}/employees/${editEmployee.id}/role`,
          { new_role: editForm.role },
          { headers: getAuthHeaders() }
        );
      }
      toast.success("Employee updated");
      setShowEditDialog(false);
      fetchEmployees();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to update employee");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (emp) => {
    setEditEmployee(emp);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editEmployee) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/employees/${editEmployee.id}`, {
        headers: getAuthHeaders(),
        
      });
      toast.success(`${editEmployee.first_name} ${editEmployee.last_name} deleted`);
      setShowDeleteDialog(false);
      fetchEmployees();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to delete employee");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPwClick = (emp) => {
    setResetPwEmployee(emp);
    setNewPassword("");
    setConfirmPassword("");
    setShowResetPwDialog(true);
  };

  const handleResetPwConfirm = async () => {
    if (!resetPwEmployee) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setResettingPw(true);
    try {
      await axios.put(`${API}/employees/${resetPwEmployee.id}`, { password: newPassword }, { headers: getAuthHeaders() });
      toast.success(`Password reset for ${resetPwEmployee.first_name} ${resetPwEmployee.last_name}. They will be prompted to change it on next login.`);
      setShowResetPwDialog(false);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to reset password");
    } finally {
      setResettingPw(false);
    }
  };

  const togglePosition = (name) => {
    setEditForm(prev => ({
      ...prev,
      positions: prev.positions.includes(name)
        ? prev.positions.filter(p => p !== name)
        : [...prev.positions, name]
    }));
  };

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="employee-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Employee List</h1>
          <p className="text-[#666666]">{employees.length} employees in the system</p>
        </div>
      </div>

      {/* Search */}
      <Card className="border border-[#E5E5E5] shadow-none">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999999]" />
            <Input
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#E5E5E5] focus:border-[#6A1E2E]"
              data-testid="search-employees"
            />
          </div>
        </CardContent>
      </Card>

      {/* Employee Table */}
      <Card className="border border-[#E5E5E5] shadow-none">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-[#1A1A1A]">
            <Users className="w-5 h-5 text-[#6A1E2E]" />
            Employees
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-[#666666]">Loading employees...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header-uppercase">Name</TableHead>
                  <TableHead className="table-header-uppercase">Email</TableHead>
                  <TableHead className="table-header-uppercase">Phone</TableHead>
                  <TableHead className="table-header-uppercase">Positions</TableHead>
                  <TableHead className="table-header-uppercase">Role</TableHead>
                  <TableHead className="table-header-uppercase">Last Login</TableHead>
                  <TableHead className="table-header-uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-[#999999]">
                      No employees found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((emp) => (
                    <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#F0EAEB] flex items-center justify-center text-[#6A1E2E] text-sm font-semibold">
                            {emp.first_name?.[0]}{emp.last_name?.[0]}
                          </div>
                          <span className="font-medium text-[#1A1A1A]">
                            {emp.first_name} {emp.last_name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#666666]">{emp.email}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-[#666666]">
                          <Phone className="w-3 h-3" />
                          {emp.phone || "—"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {emp.positions?.slice(0, 2).map((pos, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-[#FAFAFA] text-[#666666] text-xs rounded-sm border border-[#E5E5E5]"
                            >
                              {pos}
                            </span>
                          ))}
                          {emp.positions?.length > 2 && (
                            <span className="px-2 py-0.5 text-[#999999] text-xs">
                              +{emp.positions.length - 2}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-sm capitalize ${
                          emp.role === "admin" ? "bg-[#6A1E2E] text-white" : emp.role === "supervisor" ? "bg-blue-100 text-blue-800" : "bg-[#F0EAEB] text-[#6A1E2E]"
                        }`}>
                          {emp.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-[#666666] text-sm">
                          <LogIn className="w-3 h-3" />
                          {emp.last_login ? (
                            <span title={format(parseISO(emp.last_login), "MMM d, yyyy h:mm a")}>
                              {formatDistanceToNow(parseISO(emp.last_login), { addSuffix: true })}
                            </span>
                          ) : (
                            <span className="text-[#999999]">Never</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewSchedule(emp)}
                            className="text-[#6A1E2E] hover:bg-[#F0EAEB] h-8 w-8 p-0"
                            data-testid={`view-schedule-${emp.id}`}
                          >
                            <Calendar className="w-4 h-4" />
                          </Button>
                          {canManage && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClick(emp)}
                                className="text-[#6A1E2E] hover:bg-[#F0EAEB] h-8 w-8 p-0"
                                data-testid={`edit-employee-${emp.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetPwClick(emp)}
                                className="text-amber-600 hover:bg-amber-50 h-8 w-8 p-0"
                                title="Reset Password"
                                data-testid={`reset-pw-${emp.id}`}
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(emp)}
                                className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                                data-testid={`delete-employee-${emp.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#6A1E2E]" />
              {selectedEmployee?.first_name} {selectedEmployee?.last_name}'s Schedule
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between py-2">
            <Button variant="ghost" size="icon" onClick={() => setScheduleMonth(new Date(scheduleMonth.setMonth(scheduleMonth.getMonth() - 1)))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium">{format(scheduleMonth, "MMMM yyyy")}</span>
            <Button variant="ghost" size="icon" onClick={() => setScheduleMonth(new Date(scheduleMonth.setMonth(scheduleMonth.getMonth() + 1)))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {shiftsLoading ? (
              <div className="text-center py-8 text-[#666666]">Loading schedule...</div>
            ) : employeeShifts.length === 0 ? (
              <div className="text-center py-8 text-[#999999]">No scheduled shifts for this month</div>
            ) : (
              employeeShifts.map((shift) => (
                <div key={shift.id} className="p-3 bg-[#F0EAEB] border border-[#E5E5E5] rounded-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-[#1A1A1A]">{shift.event_name}</p>
                      <div className="flex items-center gap-2 text-sm text-[#666666] mt-1">
                        <Clock className="w-3 h-3" />
                        <span className="military-time">{shift.start_time} - {shift.end_time}</span>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-[#6A1E2E] bg-white px-2 py-1 rounded-sm">
                      {format(parseISO(shift.date), "EEE, MMM d")}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#6A1E2E]" />
              Edit Employee
            </DialogTitle>
            <DialogDescription>
              Update {editEmployee?.first_name} {editEmployee?.last_name}'s information.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input value={editForm.first_name} onChange={e => setEditForm(p => ({ ...p, first_name: e.target.value }))} className="border-[#E5E5E5]" data-testid="edit-first-name" />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input value={editForm.last_name} onChange={e => setEditForm(p => ({ ...p, last_name: e.target.value }))} className="border-[#E5E5E5]" data-testid="edit-last-name" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} className="border-[#E5E5E5]" data-testid="edit-email" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} className="border-[#E5E5E5]" data-testid="edit-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Street</Label>
                <Input value={editForm.street} onChange={e => setEditForm(p => ({ ...p, street: e.target.value }))} className="border-[#E5E5E5]" data-testid="edit-street" />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={editForm.city} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} className="border-[#E5E5E5]" data-testid="edit-city" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>State</Label>
                <Input value={editForm.state} onChange={e => setEditForm(p => ({ ...p, state: e.target.value }))} className="border-[#E5E5E5]" data-testid="edit-state" />
              </div>
              <div className="space-y-2">
                <Label>Zip Code</Label>
                <Input value={editForm.zip_code} onChange={e => setEditForm(p => ({ ...p, zip_code: e.target.value }))} className="border-[#E5E5E5]" data-testid="edit-zip" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Positions</Label>
              <DropdownMenu open={posDropOpen} onOpenChange={setPosDropOpen}>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-between border-[#E5E5E5]">
                    <span className="text-[#666666]">{editForm.positions.length === 0 ? "Select positions..." : `${editForm.positions.length} selected`}</span>
                    <ChevronDown className="w-4 h-4 text-[#999999]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[280px]">
                  {positions.map(pos => (
                    <DropdownMenuCheckboxItem key={pos.id} checked={editForm.positions.includes(pos.name)} onCheckedChange={() => togglePosition(pos.name)}>{pos.name}</DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {editForm.positions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {editForm.positions.map(p => (
                    <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#6A1E2E] text-white text-xs rounded-sm">
                      {p}
                      <button type="button" onClick={() => togglePosition(p)} className="hover:bg-white/20 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="border-[#E5E5E5]" data-testid="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Seniority Date</Label>
              <Input type="date" value={editForm.seniority_date} onChange={e => setEditForm(p => ({ ...p, seniority_date: e.target.value }))} className="border-[#E5E5E5]" data-testid="edit-seniority" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-[#E5E5E5]">Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#6A1E2E] hover:bg-[#521422] text-white" data-testid="save-edit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              Delete Employee
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{editEmployee?.first_name} {editEmployee?.last_name}</strong>? This will also unassign them from all shifts. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-[#E5E5E5]">Cancel</Button>
            <Button onClick={handleDeleteConfirm} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-employee">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete Employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPwDialog} onOpenChange={setShowResetPwDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-600" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{resetPwEmployee?.first_name} {resetPwEmployee?.last_name}</strong>. They will be required to change it on their next login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="border-[#E5E5E5]"
                data-testid="reset-pw-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowResetPwDialog(false)} className="border-[#E5E5E5]">Cancel</Button>
            <Button
              onClick={handleResetPwConfirm}
              disabled={resettingPw || newPassword.length < 6}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-reset-pw"
            >
              {resettingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeList;
